import { z } from "zod";
// Tracking API. Self-reported weight + GLP-1 dose logging, the composed GLP-1
// tracker view, and per-user tracking profile (height, goal, display unit,
// reminder opt-in). All measurements are canonical metric (kg/cm); the client
// converts to the user's display unit at the edge. See ADR-0016 / ADR-0017.
export const WeightUnit = z.enum(["kg", "lb"]);
// ---- Profile ----------------------------------------------------------------
export const TrackingProfile = z.object({
    heightCm: z.number().positive().nullable(),
    goalWeightKg: z.number().positive().nullable(),
    weightUnit: WeightUnit,
    doseReminders: z.boolean(),
});
export const UpdateTrackingProfileRequest = z.object({
    heightCm: z.number().positive().nullable().optional(),
    goalWeightKg: z.number().positive().nullable().optional(),
    weightUnit: WeightUnit.optional(),
    doseReminders: z.boolean().optional(),
});
// ---- Entries ----------------------------------------------------------------
export const WeightEntry = z.object({
    id: z.number(),
    kg: z.number(),
    bmi: z.number().nullable(),
    occurredAt: z.string(),
    // Provenance: "self-report" for hand-logged, otherwise the device/integration
    // source (e.g. "withings", "apple-health"). Only self-report entries are
    // editable/deletable by the user (see ADR-0016).
    source: z.string(),
    editable: z.boolean(),
});
export const DoseEntry = z.object({
    id: z.number(),
    medication: z.string(),
    doseAmount: z.number(),
    doseUnit: z.literal("mg"),
    injectionSite: z.string().nullable(),
    note: z.string().nullable(),
    cadenceDays: z.number(),
    occurredAt: z.string(),
});
// ---- Requests ---------------------------------------------------------------
export const LogDoseRequest = z.object({
    medication: z.string().min(1),
    doseAmount: z.number().positive(),
    injectionSite: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    cadenceDays: z.number().min(1).max(90).optional(),
    occurredAt: z.string().datetime().optional(),
});
export const EditDoseRequest = z.object({
    medication: z.string().min(1).optional(),
    doseAmount: z.number().positive().optional(),
    injectionSite: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    cadenceDays: z.number().min(1).max(90).optional(),
    occurredAt: z.string().datetime().optional(),
});
export const LogWeightRequest = z.object({
    kg: z.number().positive(),
    occurredAt: z.string().datetime().optional(),
    note: z.string().nullable().optional(),
});
export const EditWeightRequest = z.object({
    kg: z.number().positive().optional(),
    occurredAt: z.string().datetime().optional(),
});
export const TrackingIdParam = z.object({
    id: z.string().regex(/^\d+$/),
});
// ---- Composed GLP-1 view ----------------------------------------------------
// Self-reported adherence proxy. Deliberately NOT named PDC: it is derived from
// user-logged doses, not pharmacy claims, and must never be presented as the
// payer-grade claims metric. See ADR-0016.
export const SelfReportedAdherence = z.object({
    windowDays: z.number(),
    dosesLogged: z.number(),
    expectedDoses: z.number(),
    percent: z.number(),
});
export const TrackingStats = z.object({
    latestWeightKg: z.number().nullable(),
    startWeightKg: z.number().nullable(),
    totalChangeKg: z.number().nullable(),
    percentChange: z.number().nullable(),
    weeklyAvgChangeKg: z.number().nullable(),
    bmi: z.number().nullable(),
    toGoalKg: z.number().nullable(),
});
export const CurrentDose = z.object({
    medication: z.string(),
    doseAmount: z.number(),
    doseUnit: z.literal("mg"),
    cadenceDays: z.number(),
});
export const Glp1TrackingResponse = z.object({
    profile: TrackingProfile,
    weights: z.array(WeightEntry),
    doses: z.array(DoseEntry),
    currentDose: CurrentDose.nullable(),
    nextDoseDueAt: z.string().nullable(),
    reminderScheduledFor: z.string().nullable(),
    adherence: SelfReportedAdherence.nullable(),
    stats: TrackingStats,
});
export const CreatedEntryResponse = z.object({ id: z.number() });
export const TrackingOkResponse = z.object({ ok: z.boolean() });
//# sourceMappingURL=tracking.js.map